import React, { useState, useRef, useContext } from "react";
import "../../profileAdminCard/adminModals.css";
import "./profilePhotoModal.css";
import companyBasedRequest from "../../../request/companyBasedRequest";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import { Camera, TriangleAlert } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";

const ProfileUploadModal = ({ staffProfile, isOpen, onClose, onUploaded }) => {
	useModalDismiss(onClose, { enabled: isOpen });
	const { userData, language } = useContext(CombinedContext);

	const fileRef = useRef(null);
	const [preview, setPreview] = useState(null);
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState("");

	if (!isOpen) return null;

	const t = translations[language].teamPage;
	const photoT = t.modal.photo;

	const pickFile = () => fileRef.current?.click();

	const handleFile = (e) => {
		const f = e.target.files[0];
		if (!f) return;

		if (!f.type.startsWith("image/")) {
			setError(photoT.invalid);
			return;
		}

		setError("");
		setFile(f);
		setPreview(URL.createObjectURL(f));
	};

	async function shrinkImage(file, maxWidth = 1080, quality = 0.8) {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const reader = new FileReader();

			reader.onload = (e) => {
				img.src = e.target.result;
			};

			img.onload = () => {
				const canvas = document.createElement("canvas");
				const scale = maxWidth / img.width;
				const newWidth = img.width > maxWidth ? maxWidth : img.width;
				const newHeight = img.height > maxWidth ? img.height * scale : img.height;

				canvas.width = newWidth;
				canvas.height = newHeight;

				const ctx = canvas.getContext("2d");
				ctx.drawImage(img, 0, 0, newWidth, newHeight);

				canvas.toBlob(
					(blob) => {
						resolve(blob);
					},
					"image/jpeg",
					quality // compression quality (0.8)
				);
			};

			img.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	const handleUpload = async () => {
		if (!file) {
			setError(photoT.pickFirst);
			return;
		}

		try {
			setUploading(true);
			setError("");

			// ⬅️ SHRINK IT FIRST
			const shrunkBlob = await shrinkImage(file, 1080, 0.8);

			// convert blob → base64
			const base64 = await new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result.split(",")[1]);
				reader.onerror = reject;
				reader.readAsDataURL(shrunkBlob);
			});

			const userId = staffProfile.staffId || staffProfile.userId;

			const result = await companyBasedRequest.uploadProfilePhoto(
				userData.user.shopId,
				userId,
				base64,
				"image/jpeg",
				userData.cognitoTokens.AccessToken
			);

			if (onUploaded) onUploaded(result.photoUrl);
			onClose();
		} catch (err) {
			console.error(err);
			setError(photoT.failed);
		} finally {
			setUploading(false);
		}
	};

	const initial = (staffProfile?.firstName || staffProfile?.fullName || "?").charAt(0).toUpperCase();

	return (
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal is-compact">
				{/* HEADER */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{photoT.title}</h2>
						<p className="sf-pm-sub">{photoT.description}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
						✕
					</button>
				</div>

				{/* BODY */}
				<div className="sf-pm-body">
					<section className="sf-pm-section">
						{/* Round, because that is the shape it will be used at
						    everywhere else — a square crop preview lies. */}
						<div className="sf-pm-avatar-stage">
							{preview ? (
								<img src={preview} alt="" className="sf-pm-avatar-preview" />
							) : staffProfile?.photo ? (
								<img src={staffProfile.photo} alt="" className="sf-pm-avatar-preview" />
							) : (
								<span className="sf-pm-avatar-fallback">{initial}</span>
							)}
						</div>

						{error && (
							<p className="sf-pm-hint is-error">
								<TriangleAlert size={16} strokeWidth={1.9} />
								<span>{error}</span>
							</p>
						)}

						<input
							type="file"
							accept="image/*"
							ref={fileRef}
							style={{ display: "none" }}
							onChange={handleFile}
						/>

						<button type="button" className="sf-pm-add" onClick={pickFile} disabled={uploading}>
							<Camera size={16} strokeWidth={1.9} />
							{preview ? photoT.change : photoT.choose}
						</button>
					</section>
				</div>

				{/* FOOTER */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={uploading}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button
						className="sf-pm-btn is-primary"
						onClick={handleUpload}
						disabled={uploading || !file}
					>
						{uploading && <span className="sf-pm-spinner" aria-hidden="true" />}
						{uploading ? photoT.uploading : photoT.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ProfileUploadModal;
